import type { QueryClient } from "@tanstack/react-query";
import { getTeacherUpcomingPrivateLessonsRange } from "@/lib/dashboard/teacher-dashboard-date-range";
import { getTeacherPrivateLessonsCalendarRange } from "@/lib/services/privateLessonsCalendarRange";
import { QueryKeys } from "@/lib/types";
import { DashboardWidgetId } from "@/lib/types/dashboard-widgets";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import type { StudioFeatureSettings } from "@/lib/utils/feature-settings/feature-settings-defaults";
import type { TenantWithRegion } from "@/lib/utils/tenant-account";
import { prefetchStudioFeatureSettings } from "./prefetchMemberDashboardMemberSettings";
import { prefetchPrivateLessonsCalendar } from "./prefetchPrivateLessonsCalendar";
import { prefetchTeacherClassEnrollments } from "./prefetchTeacherClassEnrollments";
import { prefetchTeacherClasses } from "./prefetchTeacherClasses";
import { prefetchTenantGet } from "./prefetchTenantGet";
import { prefetchWidgetSettings } from "./prefetchWidgetSettings";

/** Server prefetch for teacher role on `/admin/dashboard`. */
export async function prefetchTeacherDashboard(
  queryClient: QueryClient,
  userId: number,
  accessToken: string,
  appointmentTeacherId: number = userId,
) {
  const tenantId = resolveEffectiveTenantNumber();
  await prefetchTenantGet(queryClient, tenantId, accessToken);
  const tenant = queryClient.getQueryData<TenantWithRegion>([
    QueryKeys.Tenant,
    tenantId,
  ]);
  const timeZone = tenant?.timeZone;

  const layouts = await prefetchWidgetSettings(
    queryClient,
    tenantId,
    accessToken,
  );
  const teacherLayout = layouts.Teacher;

  const { classIds } = await prefetchTeacherClasses(
    queryClient,
    userId,
    accessToken,
  );

  const tasks: Promise<unknown>[] = [];

  if (
    teacherLayout.regularWidgets.includes(DashboardWidgetId.RecentActivity) &&
    classIds.length > 0
  ) {
    tasks.push(
      prefetchTeacherClassEnrollments(queryClient, classIds, accessToken),
    );
  }

  await prefetchStudioFeatureSettings(tenantId, queryClient, accessToken);
  const studioFeatures = queryClient.getQueryData<StudioFeatureSettings>([
    QueryKeys.StudioFeatureSettings,
    tenantId,
  ]);
  // A failed prefetch leaves the cache empty. A missing key means on.
  const appointmentsEnabled = studioFeatures?.appointmentsEnabled ?? true;

  if (appointmentsEnabled) {
    const range7 = getTeacherUpcomingPrivateLessonsRange();
    tasks.push(
      prefetchPrivateLessonsCalendar(
        queryClient,
        accessToken,
        {
          startDate: range7.start,
          endDate: range7.end,
          teacherId: appointmentTeacherId,
        },
        timeZone,
      ),
    );

    if (
      teacherLayout.regularWidgets.includes(
        DashboardWidgetId.PrivateLessonsCalendar,
      )
    ) {
      const teacherCalendarRange = getTeacherPrivateLessonsCalendarRange();
      tasks.push(
        prefetchPrivateLessonsCalendar(
          queryClient,
          accessToken,
          {
            startDate: teacherCalendarRange.start,
            endDate: teacherCalendarRange.end,
            teacherId: appointmentTeacherId,
          },
          timeZone,
        ),
      );
    }
  }

  await Promise.allSettled(tasks);

  return queryClient;
}
